"use client";

import { useState, useMemo } from "react";
import { postWithAuth, deleteWithAuth } from "@/lib/api-helpers";
import { toast } from "@/lib/toast";
import type { Members } from "@/types/workspace";
import { useWorkspaceContext } from "@/contexts/workspaceContext";
import { DropdownMenu, DropdownMenuIcons } from "@/components/tailwind/ui/dropdown-menu";
import type { DropdownMenuItemProps } from "@/components/tailwind/ui/dropdown-menu";
import { AnchoredPopover } from "@/components/tailwind/ui/anchored-popover";
import DeleteConfirmationModal from "@/components/tailwind/ui/deleteConfirmationModal";

interface MemberActionsMenuProps {
  member: Members;
  workspaceId: string;
  isCurrentUser?: boolean;
  currentUserRole?: "owner" | "admin" | "member" | null;
  buttonRef?: React.RefObject<HTMLButtonElement>;
  onClose: () => void;
  // onMemberUpdated: () => void;
}

export default function MemberActionsMenu({
  member,
  workspaceId,
  isCurrentUser = false,
  currentUserRole = null,
  buttonRef,
  onClose,
  // onMemberUpdated,
}: MemberActionsMenuProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const { setCurrentWorkspace, currentWorkspace } = useWorkspaceContext();

  const handleUpdateRole = async (newRole: "admin" | "member") => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      const response = await postWithAuth(
        "/api/work-space/settings/people/update-role",
        {
          workspaceId,
          memberId: member.userId,
          role: newRole,
        }
      );

      if (response && !(response as { isError?: boolean }).isError) {
        // Update local workspace state
        if (currentWorkspace) {
          const updatedMembers = (currentWorkspace.members || []).map((m) =>
            m.userId === member.userId ? { ...m, role: newRole } : m
          );
          setCurrentWorkspace({
            ...currentWorkspace,
            members: updatedMembers,
          });
        }

        toast.success(
          `${member.userName || member.userEmail} is now ${newRole}`
        );
        // onMemberUpdated();
        onClose();
      } else {
        throw new Error("Failed to update role");
      }
    } catch (error) {
      console.error("Error updating role:", error);
      toast.error("Failed to update member role");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveMember = async () => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      const response = await postWithAuth(
        "/api/work-space/settings/people/remove",
        {
          workspaceId,
          memberId: member.userId,
        }
      );

      if (response && !(response as { isError?: boolean }).isError) {
        // Update local workspace state
        if (currentWorkspace) {
          const updatedMembers = (currentWorkspace.members || []).filter(
            (m) => m.userId !== member.userId
          );
          setCurrentWorkspace({
            ...currentWorkspace,
            members: updatedMembers,
          });
        }

        toast.success(
          `${member.userName || member.userEmail} has been removed`
        );
        // onMemberUpdated();
        onClose();
      } else {
        throw new Error("Failed to remove member");
      }
    } catch (error) {
      console.error("Error removing member:", error);
      toast.error("Failed to remove member");
    } finally {
      setIsLoading(false);
    }
  };

  // Build menu items array based on conditions
  const menuItems: DropdownMenuItemProps[] = useMemo(() => {
    const items: DropdownMenuItemProps[] = [];

    // Don't allow changing own role or removing yourself
    if (!isCurrentUser) {
      // Role change options - text changes based on current role
      if (member.role === "member") {
        items.push({
          id: 'make-admin',
          label: "Make admin",
          subtitle: "Can invite and manage members",
          icon: <DropdownMenuIcons.MakeAdmin />,
          onClick: () => handleUpdateRole("admin"),
          disabled: isLoading,
        });
      }

      // Owner and admin can make admin as normal member
      if (member.role === "admin") {
        items.push({
          id: 'make-member',
          label: "Make member",
          subtitle: "Can no longer invite or manage members",
          icon: <DropdownMenuIcons.MakeMember />,
          onClick: () => handleUpdateRole("member"),
          disabled: isLoading,
        });
      }

      // Remove option - admin cannot remove owner
      const isOwnerMember = member.role === "owner";
      const isAdminUser = currentUserRole === "admin";
      
      if (!(isAdminUser && isOwnerMember)) {
        // Only show remove if: (not admin) OR (admin but not removing owner)
        items.push({
          id: 'remove',
          label: "Remove",
          icon: <DropdownMenuIcons.Delete />,
          variant: 'destructive',
          onClick: () => setShowRemoveConfirm(true),
          disabled: isLoading,
        });
      }
    } else {
      // If it's the current user, show a message or disable actions
      items.push({
        id: 'cannot-modify',
        label: "You cannot modify your own role",
        icon: <DropdownMenuIcons.MakeAdmin />,
        onClick: () => {},
        disabled: true,
      });
    }

    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCurrentUser, member.role, currentUserRole, isLoading, member.userId, workspaceId]);

  return (
    <>
      {!showRemoveConfirm && (
        <AnchoredPopover
          anchor={buttonRef ?? null}
          placement="bottom-end"
          onClose={onClose}
          className="bg-white dark:bg-zinc-900 rounded-md shadow-lg border border-component min-w-[180px] member-actions-menu"
        >
          <DropdownMenu items={menuItems} />
        </AnchoredPopover>
      )}

      <DeleteConfirmationModal
        isOpen={showRemoveConfirm}
        header="Remove member?"
        message={`${member.userName || member.userEmail} will be removed from this workspace.`}
        entity="member"
        confirmButtonText="Remove"
        confirmButtonColor="red"
        isDeleting={isLoading}
        onCancel={() => {
          setShowRemoveConfirm(false);
          onClose();
        }}
        onConfirm={handleRemoveMember}
      />
    </>
  );
}
